import { sortBy } from 'lodash';
import moment from 'moment';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import { useLoggedInEmployeeId, useLoggedInUserRoles } from '~/components/Auth';
import { getApplicationConfig } from '~/data/applicationConfig';
import { CompanyData, fetchCompanyData } from '~/data/company';
import { useCompanyData } from '~/data/company/useCompanyData';
import { useAllEmployees } from '~/data/employees';
import { Employees } from '~/data/employees/types';
import { useAllOrgUnits } from '~/data/orgunits';
import {
  getMyAltDirUnits,
  getMyDeputyDirUnits,
  getTeamEmployeeList,
  sortSearchItems,
} from '~/data/search';
import { Roles } from '~/data/user';
import {
  isRoleAltManager,
  isRoleDepManager,
  isRoleHR,
  isRoleHRSalary,
  isRoleITAdmin,
  isRoleManager,
  isRoleRecruiter,
} from '~/services/Permissions';
import { AppDispatch } from '~/store';

export type GroupedOrgUnitEmps = {
  name: string;
  number: string;
  employees: Employees[];
};

export type UseEmployeesSearchOutput = {
  allEmployees: Employees[];
  getSearchResults: (searchQuery: string) => Employees[];
  getTeamMemberList: () => Employees[];
  getMatrixTeamMemberList: () => Employees[];
  getNewEmployeeList: () => Employees[];
  getEditableEndedEmpList: () => Employees[];
  getGroupedOrgUnitEmployees: () => GroupedOrgUnitEmps[];
  showEditableEndedEmployeeTab: boolean;
  hasMatrixTeam: boolean;
};

const getFilteredEmployees = (
  allEmployees: Employees[] | undefined,
  myUnits: number[],
  myId: number,
) =>
  allEmployees?.filter(
    (emp) => myUnits.indexOf(emp.fEmpUnit) >= 0 && emp.fEmpId !== myId,
  ) || [];

const getMatrixTeamEmployees = (
  loggedInEmpId: number,
  loggedInEmpRoles: Roles[] | string[] = [],
  allEmployees: Employees[] = [],
  secondaryTreeMatrixEmpList: Employees[] = [],
  loggedInDepDirUnits: number[] = [],
) => {
  if (isRoleDepManager(loggedInEmpRoles) && loggedInDepDirUnits.length) {
    return [
      ...secondaryTreeMatrixEmpList,
      ...getFilteredEmployees(allEmployees, loggedInDepDirUnits, loggedInEmpId),
    ];
  }

  return secondaryTreeMatrixEmpList;
};

const findTeamEmployees = (
  loggedInEmpId: number,
  loggedInEmpRoles: Roles[] | string[] = [],
  allEmployees: Employees[] = [],
  teamEmployeeList: Employees[] = [],
  loggedInDepDirUnits: number[] = [],
  loggedInAltDirUnits: number[] = [],
): Employees[] => {
  if (isRoleDepManager(loggedInEmpRoles) && loggedInDepDirUnits.length) {
    return [
      ...teamEmployeeList,
      ...getFilteredEmployees(allEmployees, loggedInDepDirUnits, loggedInEmpId),
    ];
  }

  if (isRoleAltManager(loggedInEmpRoles) && loggedInAltDirUnits.length) {
    return [
      ...teamEmployeeList,
      ...getFilteredEmployees(allEmployees, loggedInAltDirUnits, loggedInEmpId),
    ];
  }

  return teamEmployeeList;
};

const fetchAllSearchData = async (
  loggedInEmpId: number,
  loggedInEmpRoles: Roles[] | string[],
) =>
  Promise.all([
    isRoleManager(loggedInEmpRoles)
      ? getTeamEmployeeList(1, loggedInEmpId)
      : Promise.resolve([]),
    isRoleDepManager(loggedInEmpRoles)
      ? getMyDeputyDirUnits(loggedInEmpId)
      : Promise.resolve([]),
    isRoleAltManager(loggedInEmpRoles)
      ? getMyAltDirUnits(loggedInEmpId)
      : Promise.resolve([]),
    getApplicationConfig(),
  ]).then(
    ([
      teamEmployeeList,
      myDeputyDirUnits,
      myAltDirUnits,
      applicationConfig,
    ]) => {
      if (!isRoleManager(loggedInEmpRoles)) {
        return Promise.resolve({
          teamEmployeeList,
          myDeputyDirUnits,
          myAltDirUnits,
          matrixTeamEmployeeList: [],
          hasMatrixTeam: false,
        });
      }

      const secondaryTreeId =
        applicationConfig?.fComConfig.coreConfig.fComSecondaryTreeId;

      return getTeamEmployeeList(secondaryTreeId || 1, loggedInEmpId).then(
        (matrixTeamEmployeeList) => ({
          teamEmployeeList,
          myDeputyDirUnits,
          myAltDirUnits,
          matrixTeamEmployeeList,
          hasMatrixTeam: !!(
            secondaryTreeId &&
            matrixTeamEmployeeList.find(
              (emp) => emp.fEmpManagerId === loggedInEmpId,
            )
          ),
        }),
      );
    },
  );

const getSearchableItems = (
  allEmployees?: Employees[],
  newEmployees?: Employees[],
  editableEndedEmployees?: Employees[],
  companyData?: CompanyData,
) => {
  const searchEmps = (allEmployees || []).concat(newEmployees || []);
  var editingLimit =
    companyData?.config?.fComFormerEmployeeDataEditingLimit || 0;

  var endedEmps = (editableEndedEmployees || []).filter((emp) => {
    return moment().diff(emp.fEmplEndDate, 'days') <= editingLimit;
  });

  return editingLimit > 0 && editingLimit <= 180
    ? searchEmps.concat(endedEmps)
    : searchEmps;
};

const findMatchingItems = (
  searchQuery: string,
  searchableItems: Employees[],
) => {
  const searchString = searchQuery.toLowerCase();

  return sortSearchItems(
    searchableItems?.filter(
      (item) =>
        item.fEmpFirstName.toLowerCase().indexOf(searchString) === 0 ||
        item.fEmpLastName.toLowerCase().indexOf(searchString) === 0 ||
        item.fEmpPosition.toLowerCase().indexOf(searchString) === 0 ||
        item.fEmpPersonNumber.toLowerCase().indexOf(searchString) === 0 ||
        (item.fEmpLastName + ' ' + item.fEmpFirstName)
          .toLowerCase()
          .indexOf(searchString) === 0 ||
        (item.fEmpFirstName + ' ' + item.fEmpLastName)
          .toLowerCase()
          .indexOf(searchString) === 0,
    ),
  );
};

export const useSearchData = (): UseEmployeesSearchOutput => {
  const dispatch: AppDispatch = useDispatch();
  const [rawEmpRelationData, setRawEmpRelationData] = useState<{
    teamEmployeeList: Employees[];
    myDeputyDirUnits: number[];
    myAltDirUnits: number[];
    matrixTeamEmployeeList: Employees[];
    hasMatrixTeam?: boolean;
  }>({
    teamEmployeeList: [],
    myDeputyDirUnits: [],
    myAltDirUnits: [],
    matrixTeamEmployeeList: [],
  });
  const myId = useLoggedInEmployeeId();
  const myRoles = useLoggedInUserRoles();
  const { companyData } = useCompanyData();
  const { orgUnits } = useAllOrgUnits();

  const {
    activeEmployees: activeEmployeesObject,
    newEmployees: newEmployeesObject,
    editableEndedEmployees: editableEndedEmployeesObject,
  } = useAllEmployees();

  const showEditableEndedEmployeeTab = useMemo(
    () =>
      !!companyData?.config?.fComFormerEmployeeDataEditingLimit &&
      (isRoleHR(myRoles) ||
        isRoleITAdmin(myRoles) ||
        isRoleRecruiter(myRoles)) &&
      !!Object.keys(editableEndedEmployeesObject).length,
    [companyData, myRoles, editableEndedEmployeesObject],
  );

  const allEmployees = useMemo(
    () => sortSearchItems(Object.values(activeEmployeesObject)),
    [activeEmployeesObject],
  );

  useEffect(() => {
    let isMounted = true;

    dispatch(fetchCompanyData());
    fetchAllSearchData(myId, myRoles).then((empRelationData) => {
      if (isMounted) {
        setRawEmpRelationData(empRelationData);
      }
    });

    return () => {
      isMounted = false;
    };
  }, [dispatch, myId, myRoles, setRawEmpRelationData]);

  const getTeamMemberList = useCallback(() => {
    return sortSearchItems(
      findTeamEmployees(
        myId,
        myRoles,
        allEmployees,
        rawEmpRelationData.teamEmployeeList,
        rawEmpRelationData.myDeputyDirUnits,
        rawEmpRelationData.myAltDirUnits,
      ),
    );
  }, [myId, myRoles, allEmployees, rawEmpRelationData]);

  const getMatrixTeamMemberList = useCallback(() => {
    return sortSearchItems(
      getMatrixTeamEmployees(
        myId,
        myRoles,
        allEmployees,
        rawEmpRelationData.hasMatrixTeam
          ? rawEmpRelationData.matrixTeamEmployeeList
          : [],
        rawEmpRelationData.myDeputyDirUnits,
      ),
    );
  }, [myId, myRoles, allEmployees, rawEmpRelationData]);

  const getNewEmployeeList = useCallback(() => {
    if (
      !isRoleHR(myRoles) &&
      !isRoleITAdmin(myRoles) &&
      !isRoleManager(myRoles) &&
      !isRoleRecruiter(myRoles) &&
      !isRoleHRSalary(myRoles)
    ) {
      return [];
    }

    return sortSearchItems(Object.values(newEmployeesObject));
  }, [myRoles, newEmployeesObject]);

  const getEditableEndedEmpList = useCallback(() => {
    if (
      !isRoleHR(myRoles) &&
      !isRoleITAdmin(myRoles) &&
      !isRoleManager(myRoles) &&
      !isRoleRecruiter(myRoles)
    ) {
      return [];
    }

    return sortSearchItems(Object.values(editableEndedEmployeesObject));
  }, [myRoles, editableEndedEmployeesObject]);

  const getSearchResults = useCallback(
    (searchQuery: string) => {
      const searchItems =
        isRoleITAdmin(myRoles) || isRoleHR(myRoles) || isRoleManager(myRoles)
          ? getSearchableItems(
              allEmployees,
              getNewEmployeeList(),
              getEditableEndedEmpList(),
              companyData,
            )
          : allEmployees;

      return findMatchingItems(searchQuery, searchItems);
    },
    [
      allEmployees,
      companyData,
      myRoles,
      getEditableEndedEmpList,
      getNewEmployeeList,
    ],
  );

  const getGroupedOrgUnitEmployees = (): GroupedOrgUnitEmps[] => {
    const grouped = sortBy(orgUnits, ['fOrgUnitName']).map((orgUnit) => ({
      name: orgUnit.fOrgUnitName,
      number: orgUnit.fOrgUnitNumber,
      employees: allEmployees.filter(
        (emp) => emp.fEmpUnit === orgUnit.fOrgUnitId,
      ),
    }));

    return grouped;
  };

  return {
    allEmployees,
    getSearchResults,
    getTeamMemberList,
    getMatrixTeamMemberList,
    getNewEmployeeList,
    getEditableEndedEmpList,
    getGroupedOrgUnitEmployees,
    showEditableEndedEmployeeTab,
    hasMatrixTeam: !!rawEmpRelationData.hasMatrixTeam,
  };
};
